import { useQueryClient } from "@tanstack/solid-query";
import { For, Show, createSignal } from "solid-js";

import {
  type CustomRecordImportResponse,
  downloadCustomRecordsCsv,
  uploadCustomRecordsCsv,
} from "~/lib/csv_io";

type Props = {
  wsKey: () => string;
  codId: () => string;
  objectKey: () => string;
  // For the "Importing into <label>" copy.
  objectLabelPlural: () => string;
};

export function CustomRecordsCsvButtons(props: Props) {
  const qc = useQueryClient();
  const [exporting, setExporting] = createSignal(false);
  const [exportError, setExportError] = createSignal<string | null>(null);

  const [showImport, setShowImport] = createSignal(false);
  const [file, setFile] = createSignal<File | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [importError, setImportError] = createSignal<string | null>(null);
  const [result, setResult] = createSignal<CustomRecordImportResponse | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setImportError(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCustomRecordsCsv(props.wsKey(), props.codId(), props.objectKey());
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (dryRun: boolean) => {
    const f = file();
    if (!f) return;
    setBusy(true);
    setImportError(null);
    try {
      const r = await uploadCustomRecordsCsv(props.wsKey(), props.codId(), f, { dryRun });
      setResult(r);
      if (!dryRun && r.created > 0) {
        qc.invalidateQueries({
          queryKey: ["custom-records", props.wsKey(), props.codId()],
        });
      }
    } catch (err) {
      setImportError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setShowImport(false);
    reset();
  };

  const lastResultWasDryRun = () => result()?.dry_run === true;
  const canCommit = () => {
    const r = result();
    if (!r) return false;
    return r.dry_run && r.created > 0 && !busy();
  };

  return (
    <>
      <button
        type="button"
        class="btn btn-sm btn-ghost"
        onClick={handleExport}
        disabled={exporting()}
        aria-label={`Export ${props.objectLabelPlural()} as CSV`}
      >
        {exporting() ? "Exporting…" : "Export CSV"}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-ghost"
        onClick={() => setShowImport(true)}
        aria-label={`Import ${props.objectLabelPlural()} from CSV`}
      >
        Import CSV
      </button>

      <Show when={exportError()}>
        <div role="alert" class="alert alert-error text-sm absolute top-16 right-4 max-w-xl">
          {exportError()}
        </div>
      </Show>

      <Show when={showImport()}>
        <dialog
          open
          class="modal modal-open"
          aria-modal="true"
          aria-labelledby="cr-csv-import-title"
        >
          <div class="modal-box max-w-2xl">
            <h3 id="cr-csv-import-title" class="font-bold text-lg mb-3">
              Import {props.objectLabelPlural().toLowerCase()} from CSV
            </h3>
            <p class="text-sm opacity-70 mb-4">
              <code>name</code> is required. Custom fields use <code>cf:&lt;key&gt;</code> columns;
              header case + spaces + dashes are normalised.{" "}
              <strong>Preview with a dry-run first.</strong>
            </p>

            <div class="form-control mb-4">
              <label class="label" for="cr-csv-file">
                <span class="label-text">CSV file</span>
              </label>
              <input
                id="cr-csv-file"
                type="file"
                accept=".csv,text/csv"
                class="file-input file-input-bordered file-input-sm w-full"
                onChange={(e) => {
                  setFile(e.currentTarget.files?.[0] ?? null);
                  setResult(null);
                }}
              />
            </div>

            <Show when={importError()}>
              <div role="alert" class="alert alert-error text-sm mb-3">
                {importError()}
              </div>
            </Show>

            <Show when={result()}>
              {(r) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{r().total}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Created</div>
                      <div class="stat-value text-lg text-success">{r().created}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Rejected</div>
                      <div class="stat-value text-lg text-error">{r().rejected}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Mode</div>
                      <div class="stat-value text-lg">{r().dry_run ? "Preview" : "Committed"}</div>
                    </div>
                  </div>
                  <Show when={r().rejected > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Rejected rows"
                    >
                      <table class="table table-xs">
                        <thead>
                          <tr>
                            <th>Line</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          <For each={r().rows.filter((row) => !row.ok)}>
                            {(row) => (
                              <tr>
                                <td>{row.line_no}</td>
                                <td class="text-xs">{row.error}</td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={close} disabled={busy()}>
                Close
              </button>
              <button
                type="button"
                class="btn"
                onClick={() => handleImport(true)}
                disabled={!file() || busy()}
              >
                {busy() && lastResultWasDryRun() ? "Validating…" : "Preview (dry-run)"}
              </button>
              <button
                type="button"
                class="btn btn-primary"
                onClick={() => handleImport(false)}
                disabled={!canCommit()}
              >
                {busy() && !lastResultWasDryRun() ? "Committing…" : "Commit"}
              </button>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
